/**
 * D1 (22/10/2026) — sources cited in the side chat with « @ ». A cited source
 * is the scope of the turn: the chat reads in it and cites what it uses.
 * A reference sent by the browser is untrusted transport: the server reloads
 * it under the person's rights before reading.
 * Conception: implementation-c/conception-sources-chat-2026-10-22.md.
 */

export type ChatSourceRef =
  | { kind: 'knowledge_folder'; folderId: string }
  | { kind: 'knowledge_document'; documentId: string }
  /** D4: every document carrying a label (« #objection ») that the person reads. */
  | { kind: 'knowledge_tag'; tagId: string }
  /** D2: the list of an app, filtered on labels when some are given (« Prospects SDR · #chaud »). */
  | { kind: 'app_list'; appId: string; labelIds: string[] }
  /** D2: a folder of objects across apps (/folders). */
  | { kind: 'app_folder'; folderId: string }
  /** D3: a Domino account (company), by its UUID. */
  | { kind: 'account'; subjectId: string }
  /** G3: one object an answer cited, kept for the next question (its list is read with it); never a chip. */
  | { kind: 'app_record'; appId: string; recordId: string };

export interface ChatSource {
  ref: ChatSourceRef;
  /** Label of the chip; recomputed by the server from what it reloads. */
  label: string;
}

/** What the « @ » picker offers (search_chat_sources_d1). */
export interface ChatSourceOption extends ChatSource {
  /** Folder of a document, number of documents of a folder. */
  detail: string | null;
}

/** One numbered citation of a sourced answer; `n` is the [n] marker in the text. */
export type ChatCitation =
  | {
      n: number;
      kind: 'document';
      documentId: string;
      title: string;
      folder: string;
      excerpt: string;
    }
  | { n: number; kind: 'web'; title: string; url: string }
  /** D2: an object of an app list, opened at /apps/:appKey/o/:recordId. */
  | { n: number; kind: 'record'; appKey: string; app: string; recordId: string; title: string }
  /** D3: an account, opened at /threads/:subjectId. */
  | { n: number; kind: 'account'; subjectId: string; title: string }
  /**
   * 29/10: a list filtered by the chat (« 51 offres à Paris »), opened at
   * /apps/:appKey?q=…&statut=…&etiquette=…; `title` is written by the code from what was read.
   */
  | {
      n: number; kind: 'record_set'; appKey: string; appId: string; app: string;
      query: string; status: string | null; labelId: string | null; count: number; title: string;
    };

/** A cited source the turn could not read, with why (deleted, no access). */
export interface ChatUnreadSource {
  label: string;
  reason: string;
}

export const chatSourceKey = (ref: ChatSourceRef): string => {
  if (ref.kind === 'knowledge_folder') return `folder:${ref.folderId}`;
  if (ref.kind === 'knowledge_document') return `document:${ref.documentId}`;
  if (ref.kind === 'app_list') return `list:${ref.appId}:${[...ref.labelIds].sort().join(',')}`;
  if (ref.kind === 'account') return `account:${ref.subjectId}`;
  if (ref.kind === 'knowledge_tag') return `tag:${ref.tagId}`;
  if (ref.kind === 'app_record') return `record:${ref.recordId}`;
  return `objects:${ref.folderId}`;
};
